import React, { useState, useCallback, useEffect, useMemo } from "react";
import _ from "underscore";
import { useSearchParams } from "react-router-dom";
import styles from "./educationConsolidatedData.module.css";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import FilterIcon from "../../../assets/icons/bars-filter.svg";
import SavedFiltersIcon from "../../../assets/icons/saved-filters.svg";
import DatabaseIcon from "../../../assets/icons/book.svg";
import ColumnsIcon from "../../../assets/icons/manage-columns.svg";
import MergeIcon from "../../../assets/icons/merge-tables.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import { educationConsolidatedData, getSavedEducationFilterById } from "../../../services/education";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import Button from "../../../components/button/button";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import FilterSelect from "../../../components/predictive/filterSelect/filterSelect";
import SavedFiltersModal from "../../../components/filters/savedFiltersModal/savedFiltersModal";
import SaveFilterModal from "../../../components/filters/saveFilterModal/saveFilterModal";
import FilterConfigModal from "../../../components/filters/filterConfigModal/filterConfigModal";
import ColumnManagerModal from "../../../components/filters/columnManagerModal/columnManagerModal";
import JoinTablesModal from "../../../components/filters/joinTablesModal/joinTablesModal";

const EducationConsolidatedData = () => {
  const { institution } = useInstitution();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [pendingFilters, setPendingFilters] = useState([]);
  const [currentFilterField, setCurrentFilterField] = useState("");
  const [currentFilterOperator, setCurrentFilterOperator] = useState("eq");
  const [currentFilterValues, setCurrentFilterValues] = useState([]);
  const [currentFilterValue, setCurrentFilterValue] = useState("");
  const [currentFilterCondition, setCurrentFilterCondition] = useState("AND");
  const [loadingFilterValues, setLoadingFilterValues] = useState(false);
  const [selectedMultipleValues, setSelectedMultipleValues] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showSavedFiltersModal, setShowSavedFiltersModal] = useState(false);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [showFilterConfigModal, setShowFilterConfigModal] = useState(false);
  const [showColumnManagerModal, setShowColumnManagerModal] = useState(false);
  const [showJoinTablesModal, setShowJoinTablesModal] = useState(false);
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });
  const [additionalFilters, setAdditionalFilters] = useState([]);
  // Arquivo de origem vindo da URL (?fileId=&fileName=). Restringe a listagem às linhas daquele
  // arquivo sem virar um chip de filtro; o usuário nunca vê o fileId.
  const [sourceFile, setSourceFile] = useState(null);
  
  // Estado para configuração de JOINs
  const [joinConfig, setJoinConfig] = useState({
    enabled: false,
    table: '',
    type: 'INNER',
    on: 'StudentId',
    leftKey: 'StudentId',
    rightKey: 'StudentId',
    selectFields: []
  });
  const [joinSectionExpanded, setJoinSectionExpanded] = useState(false);
  const [additionalFieldsExpanded, setAdditionalFieldsExpanded] = useState(false);
  const [selectedAdditionalFields, setSelectedAdditionalFields] = useState([]);
  
  // Estados para gerenciar colunas visíveis
  const [additionalColumnsExpanded, setAdditionalColumnsExpanded] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([
    'studentId',
    'courseTitle',
    'courseModality',
    'discipline',
    'knowledgeArea',
    'grade',
    'absences',
    'status',
    'academicPeriod',
    'yearValidity',
    'startDate',
    'endDate'
  ]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return 'R$ 0,00';
    }
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Início/término da disciplina são datas (sem hora relevante) — exibe só a data.
  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Definição de todas as colunas disponíveis (baseado no retorno da API)
  const availableColumns = useMemo(() => [
    { key: 'studentId', label: 'ID do Aluno' },
    { key: 'courseTitle', label: 'Título do Curso' },
    { key: 'courseModality', label: 'Modalidade do Curso' },
    { key: 'discipline', label: 'Disciplina' },
    { key: 'knowledgeArea', label: 'Área de Conhecimento' },
    { key: 'courseType', label: 'Tipo do Curso' },
    { key: 'courseGrade', label: 'Nota do Curso' },
    { key: 'locality', label: 'Localidade' },
    { key: 'grade', label: 'Nota' },
    { key: 'absences', label: 'Faltas' },
    { key: 'status', label: 'Status' },
    { key: 'academicPeriod', label: 'Período Acadêmico' },
    { key: 'yearValidity', label: 'Ano de Validade' },
    { key: 'startDate', label: 'Data de Início' },
    { key: 'endDate', label: 'Data de Término' },
    { key: 'createdAt', label: 'Data de Criação' },
    { key: 'updatedAt', label: 'Data de Atualização' }
  ], []);

  const filterOperators = useMemo(() => [
    { value: "eq", label: "Igual a (=)" },
    { value: "in", label: "Está em (IN)" },
    { value: "gt", label: "Maior que (>)" },
    { value: "lt", label: "Menor que (<)" },
    { value: "gte", label: "Maior ou igual (>=)" },
    { value: "lte", label: "Menor ou igual (<=)" },
    { value: "empty", label: "Vazio (sem valor)" }
  ], []);

  const filterFields = useMemo(() => [
    { value: "StudentId", label: "ID do Aluno", apiField: "StudentId" },
    { value: "StudentStatus", label: "Status do Aluno", apiField: "StudentStatus" },
    { value: "CourseId", label: "ID do Curso", apiField: "CourseId" },
    { value: "CourseName", label: "Nome do Curso", apiField: "CourseName" },
    { value: "ClassId", label: "ID da Turma", apiField: "ClassId" },
    { value: "DisciplineId", label: "ID da Disciplina", apiField: "DisciplineId" },
    { value: "DisciplineTitle", label: "Título da Disciplina", apiField: "DisciplineTitle" },
    { value: "DisciplineModality", label: "Modalidade da Disciplina", apiField: "DisciplineModality" },
    { value: "Frequency", label: "Frequência (%)", apiField: "Frequency" },
    { value: "IntermediateGrade", label: "Nota Intermediária", apiField: "IntermediateGrade" },
    { value: "FinalExamGrade", label: "Nota da Prova Final", apiField: "FinalExamGrade" },
    { value: "PassingGradeThreshold", label: "Nota Mínima para Aprovação", apiField: "PassingGradeThreshold" },
    { value: "FinalGrade", label: "Nota Final", apiField: "FinalGrade" },
    { value: "StartDate", label: "Data de Início", apiField: "StartDate" },
    { value: "EndDate", label: "Data de Término", apiField: "EndDate" },
    { value: "TeacherId", label: "ID do Docente", apiField: "TeacherId" },
    { value: "TheoreticalHours", label: "Horas Teóricas", apiField: "TheoreticalHours" },
    { value: "PracticalHours", label: "Horas Práticas", apiField: "PracticalHours" },
    { value: "ExtensionHours", label: "Horas de Extensão", apiField: "ExtensionHours" },
    { value: "TotalHours", label: "Carga Horária Total", apiField: "TotalHours" }
  ], []);

  const joinTables = useMemo(() => [
    { value: "", label: "-- Nenhuma --" },
    { value: "Turnover", label: "Financeiro" },
    { value: "Class", label: "Turma" }
  ], []);

  const joinRelationships = useMemo(() => ({
    Turnover: {
      leftKey: 'StudentId',
      rightKey: 'StudentId',
      label: 'ID do Aluno'
    },
    Class: {
      leftKey: 'ClassId',
      rightKey: 'ClassId',
      label: 'ID da Turma'
    }
  }), []);

  // Campos padrão que aparecem automaticamente
  const defaultJoinFields = useMemo(() => ({
    Turnover: ["ReceivedValue", "IssuedValue", "AccountReceiveStatus"],
    Class: ["ClassName", "KnowledgeArea", "ClassStatus"]
  }), []);

  // Todos os campos disponíveis para cada tabela
  const joinFieldsByTable = useMemo(() => ({
    Turnover: [
      { value: "ReceivedValue", label: "Valor Recebido" },
      { value: "IssuedValue", label: "Valor Emitido" },
      { value: "AccountReceiveStatus", label: "Status da Conta" },
      { value: "BillType", label: "Tipo de Cobrança" },
      { value: "PaymentType", label: "Tipo de Pagamento" },
      { value: "DueDate", label: "Data de Vencimento" }
    ],
    Class: [
      { value: "ClassName", label: "Nome da Turma" },
      { value: "KnowledgeArea", label: "Área de Conhecimento" },
      { value: "Type", label: "Tipo" },
      { value: "Grade", label: "Grau" },
      { value: "Modality", label: "Modalidade" },
      { value: "Campus", label: "Campus" },
      { value: "Location", label: "Localização" },
      { value: "Shift", label: "Turno" },
      { value: "ClassStatus", label: "Status da Turma" }
    ]
  }), []);

  const fetchConsolidatedData = useCallback((pageNumber = 1, searchTerm = "", filters = [], joins = null, dates = null, extraFilters = []) => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);
        
        // Combinar filtros principais com filtros adicionais
        const allFilters = [...filters, ...extraFilters];
        
        const params = {
          institutionId: institution.id,
          page: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: searchTerm,
          filters: allFilters
        };

        // Adicionar datas se fornecidas
        if (dates?.startDate) {
          params.startDate = dates.startDate;
        }
        if (dates?.endDate) {
          params.endDate = dates.endDate;
        }

        if (sourceFile?.id) {
          params.fileId = sourceFile.id;
        }

        // Adicionar joins se configurado
        if (joins && joins.enabled && joins.table && joins.selectFields.length > 0) {
          params.joins = [{
            table: joins.table,
            type: joins.type,
            leftKey: joins.leftKey || 'StudentId',
            rightKey: joins.rightKey || 'StudentId',
            selectFields: joins.selectFields
          }];
        }
        
        const response = await educationConsolidatedData(params);
        
        setLastApiResponse(response.educationData);
        
        setConsolidatedData(response.educationData.educationConsolidatedData);
        setPaginationInfo({
          pageNumber: response.educationData.currentPage,
          pageSize: itemsPerPage,
          totalCount: response.educationData.totalResults,
          totalPages: response.educationData.totalPages,
          hasPreviousPage: response.educationData.currentPage > 1,
          hasNextPage: response.educationData.currentPage < response.educationData.totalPages
        });
      } catch (error) {
        console.error("Erro ao buscar dados consolidados acadêmicos:", error);
        setConsolidatedData([]);
        setPaginationInfo(null);
      } finally {
        setLoading(false);
      }
    }, 1000);
    
    debouncedFetch();
  }, [institution?.id, itemsPerPage, sourceFile?.id]);

  const fetchFilterValues = useCallback(async (fieldName) => {
    if (!institution?.id || !fieldName) return;
    
    try {
      setLoadingFilterValues(true);
      
      const fieldMapping = filterFields.find(field => field.value === fieldName);
      const apiFieldName = fieldMapping ? fieldMapping.apiField : fieldName;
      
      const response = await api.client.get(
        `api/v1/EducationInstitution/FilterOptions`,
        {
          params: {
            fieldName: apiFieldName
          },
          headers: {
            institutionId: institution.id
          }
        }
      );
      
      setCurrentFilterValues(response.data?.options || []);
    } catch (error) {
      console.error("Erro ao buscar valores do filtro:", error);
      setCurrentFilterValues([]);
    } finally {
      setLoadingFilterValues(false);
    }
  }, [institution?.id, filterFields]);

  const updateURL = (filters, filterId = null) => {
    const newSearchParams = new URLSearchParams();
    
    if (searchTerm) newSearchParams.set('search', searchTerm);
    newSearchParams.set('page', currentPage.toString());
    
    if (filterId) {
      newSearchParams.set('filterId', filterId);
    } else {
      filters.forEach((filter, index) => {
        newSearchParams.set(`filter${index}_field`, filter.field);
        newSearchParams.set(`filter${index}_operator`, filter.operator || 'eq');
        newSearchParams.set(`filter${index}_values`, JSON.stringify(filter.values || []));
        newSearchParams.set(`filter${index}_groupId`, filter.groupId || 0);
        if (filter.condition) {
          newSearchParams.set(`filter${index}_condition`, filter.condition);
        }
      });
      
      const groupedFilters = {};
      filters.forEach((filter) => {
        const groupId = filter.groupId !== undefined ? filter.groupId : 0;
        if (!groupedFilters[groupId]) {
          groupedFilters[groupId] = [];
        }
        groupedFilters[groupId].push(filter);
      });
      
      if (Object.keys(groupedFilters).length > 1) {
        newSearchParams.set('groupCondition', 'AND');
      }
    }
    
    setSearchParams(newSearchParams);
  };

  const handleFilterFieldChange = (field) => {
    setCurrentFilterField(field);
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    
    if (field) {
      fetchFilterValues(field);
    }
  };

  const handleOperatorChange = (operator) => {
    setCurrentFilterOperator(operator);
    setCurrentFilterValue("");
    setSelectedMultipleValues([]);
    
    if ((operator === "eq" || operator === "in") && currentFilterField && currentFilterValues.length === 0) {
      fetchFilterValues(currentFilterField);
    }
  };

  const handleAddValueToMultiple = () => {
    if (!currentFilterValue) return;
    
    if (!selectedMultipleValues.includes(currentFilterValue)) {
      setSelectedMultipleValues([...selectedMultipleValues, currentFilterValue]);
    }
    
    setCurrentFilterValue("");
  };

  const handleRemoveValueFromMultiple = (valueToRemove) => {
    setSelectedMultipleValues(selectedMultipleValues.filter(v => v !== valueToRemove));
  };

  const handleAddFilter = () => {
    if (!currentFilterField) return;
    if (currentFilterOperator === "in" && selectedMultipleValues.length === 0) return;
    // O operador "empty" (vazio/nulo) não exige valor.
    if (currentFilterOperator !== "in" && currentFilterOperator !== "empty" && !currentFilterValue) return;
    
    const fieldMapping = filterFields.find(f => f.value === currentFilterField);
    const apiFieldName = fieldMapping ? fieldMapping.apiField : currentFilterField;
    const fieldLabel = fieldMapping ? fieldMapping.label : currentFilterField;
    const operatorMapping = filterOperators.find(op => op.value === currentFilterOperator);
    const operatorLabel = operatorMapping ? operatorMapping.label : currentFilterOperator;
    
    let groupId = 0;
    if (pendingFilters.length > 0) {
      const lastFilter = pendingFilters[pendingFilters.length - 1];
      groupId = currentFilterCondition === "OR" ? lastFilter.groupId + 1 : lastFilter.groupId;
    }
    
    const filterValues = currentFilterOperator === "in"
      ? selectedMultipleValues
      : currentFilterOperator === "empty" ? [] : [currentFilterValue];
    const displayValue = currentFilterOperator === "in"
      ? selectedMultipleValues.join(", ")
      : currentFilterOperator === "empty" ? "(vazio)" : currentFilterValue;
    
    const newFilter = {
      field: apiFieldName,
      operator: currentFilterOperator,
      values: filterValues,
      label: fieldLabel,
      operatorLabel: operatorLabel,
      displayValue: displayValue,
      condition: pendingFilters.length > 0 ? currentFilterCondition : null,
      groupId: groupId
    };
    
    const newFilters = [...pendingFilters, newFilter];
    setPendingFilters(newFilters);
    
    setCurrentFilterField("");
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    setCurrentFilterCondition("AND");
  };

  const handleRemoveFilter = (index) => {
    const newFilters = pendingFilters.filter((_, i) => i !== index);
    setPendingFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    setPendingFilters([]);
    setAppliedFilters([]);
    setCurrentFilterField("");
    setCurrentFilterOperator("eq");
    setCurrentFilterValue("");
    setCurrentFilterValues([]);
    setSelectedMultipleValues([]);
    setCurrentFilterCondition("AND");
    setCurrentPage(1);
    
    updateURL([]);
    fetchConsolidatedData(1, searchTerm, [], joinConfig, dateFilter, additionalFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters);
    setCurrentPage(1);
    
    updateURL(pendingFilters);
    fetchConsolidatedData(1, searchTerm, pendingFilters, joinConfig, dateFilter, additionalFilters);
  };

  const handleApplyFiltersFromModal = (filters) => {
    // Converter os filtros do modal para o formato usado pelo sistema
    const formattedFilters = filters.map((filter, index) => {
      const fieldMapping = filterFields.find(f => f.value === filter.field);
      const operatorMapping = filterOperators.find(op => op.value === filter.operator);
      
      // Determinar groupId baseado na condição OR
      let groupId = 0;
      if (index > 0) {
        const prevFilter = formattedFilters[index - 1];
        groupId = filter.condition === 'OR' ? (prevFilter.groupId + 1) : prevFilter.groupId;
      }
      
      return {
        field: fieldMapping ? fieldMapping.apiField : filter.field,
        operator: filter.operator,
        values: filter.values || [filter.value],
        label: fieldMapping ? fieldMapping.label : filter.field,
        operatorLabel: operatorMapping ? operatorMapping.label : filter.operator,
        displayValue: Array.isArray(filter.values) ? filter.values.join(', ') : filter.value,
        condition: filter.condition || null,
        groupId: groupId
      };
    });

    setPendingFilters(formattedFilters);
    setAppliedFilters(formattedFilters);
    setCurrentPage(1);
    updateURL(formattedFilters);
    fetchConsolidatedData(1, searchTerm, formattedFilters, joinConfig, dateFilter, additionalFilters);
  };

  const convertFiltersToApiFormat = () => {
    if (pendingFilters.length === 0) return null;

    const groupedFilters = {};
    pendingFilters.forEach((filter) => {
      const groupId = filter.groupId !== undefined ? filter.groupId : 0;
      if (!groupedFilters[groupId]) {
        groupedFilters[groupId] = [];
      }
      groupedFilters[groupId].push(filter);
    });

    const filterGroups = Object.values(groupedFilters).map((group) => {
      return {
        condition: "AND",
        filters: group.map((filter) => ({
          field: filter.field,
          operator: filter.operator === "eq" ? "=" : filter.operator,
          values: filter.values
        }))
      };
    });

    const groupCondition = "AND";

    const filterData = {
      page: 1,
      pageSize: itemsPerPage,
      groupCondition: groupCondition,
      filterGroups: filterGroups,
      source: "consolidadosAcademico"
    };

    if (joinConfig.enabled && joinConfig.table && joinConfig.selectFields.length > 0) {
      filterData.joins = [{
        table: joinConfig.table,
        type: joinConfig.type,
        leftKey: joinConfig.leftKey,
        rightKey: joinConfig.rightKey,
        selectFields: joinConfig.selectFields
      }];
    }

    return filterData;
  };

  const convertApiFilterToLocal = useCallback((savedFilter) => {
    try {
      let filterData;
      if (typeof savedFilter.filterJson === 'string') {
        filterData = JSON.parse(savedFilter.filterJson);
      } else if (savedFilter.filter) {
        filterData = savedFilter.filter;
      } else {
        filterData = savedFilter.filterJson;
      }

      if (!filterData?.filterGroups || filterData.filterGroups.length === 0) return [];

      const localFilters = [];
      let currentGroupId = 0;

      filterData.filterGroups.forEach((group, groupIndex) => {
        group.filters.forEach((filter, filterIndex) => {
          const fieldMapping = filterFields.find(f => f.apiField === filter.field);
          const operator = filter.operator === "=" ? "eq" : filter.operator;
          const operatorMapping = filterOperators.find(op => op.value === operator);
          const displayValue = Array.isArray(filter.values) ? filter.values.join(", ") : filter.values[0];
          
          localFilters.push({
            field: filter.field,
            operator: operator,
            values: filter.values,
            label: fieldMapping ? fieldMapping.label : filter.field,
            operatorLabel: operatorMapping ? operatorMapping.label : operator,
            displayValue: displayValue,
            condition: groupIndex > 0 && filterIndex === 0 ? "OR" : null,
            groupId: currentGroupId
          });
        });
        currentGroupId++;
      });

      return localFilters;
    } catch (error) {
      console.error("Erro ao converter filtro salvo:", error);
      toast.error("Erro ao processar filtro salvo");
      return [];
    }
  }, [filterFields, filterOperators]);

  const handleOpenSavedFilters = () => {
    setShowSavedFiltersModal(true);
  };

  const handleOpenSaveFilter = () => {
    if (pendingFilters.length === 0) {
      toast.warning("Adicione pelo menos um filtro antes de salvar");
      return;
    }
    setShowSaveFilterModal(true);
  };

  const handleApplySavedFilter = (savedFilter) => {
    console.log("Filtro salvo recebido:", savedFilter);
    
    const convertedFilters = convertApiFilterToLocal(savedFilter);
    console.log("Filtros convertidos:", convertedFilters);
    
    if (convertedFilters.length === 0 && !savedFilter.filterJson.includes('joins')) {
      toast.warning("Nenhum filtro encontrado para aplicar");
      return;
    }
    
    try {
      const filterData = typeof savedFilter.filterJson === 'string' 
        ? JSON.parse(savedFilter.filterJson) 
        : savedFilter.filterJson;
      
      if (filterData.joins && filterData.joins.length > 0) {
        const savedJoin = filterData.joins[0];
        setJoinConfig({
          enabled: true,
          table: savedJoin.table,
          type: savedJoin.type,
          on: savedJoin.rightKey || savedJoin.on || 'StudentId',
          leftKey: savedJoin.leftKey || 'StudentId',
          rightKey: savedJoin.rightKey || savedJoin.on || 'StudentId',
          selectFields: savedJoin.selectFields
        });
      }
    } catch (error) {
      console.error("Erro ao extrair JOIN do filtro salvo:", error);
    }
    
    setPendingFilters(convertedFilters);
    setAppliedFilters(convertedFilters);
    setCurrentPage(1);
    
    updateURL(convertedFilters, savedFilter.id);
    fetchConsolidatedData(1, searchTerm, convertedFilters, joinConfig, dateFilter, additionalFilters);
    
    toast.success(`Filtro "${savedFilter.name}" aplicado com sucesso!`);
  };

  const handleJoinTableChange = (table) => {
    const relationship = joinRelationships[table] || { leftKey: 'StudentId', rightKey: 'StudentId', label: 'ID do Aluno' };
    
    const defaultFields = table ? (defaultJoinFields[table] || []) : [];
    
    setJoinConfig({
      enabled: table !== '',
      table: table,
      type: 'INNER',
      on: relationship.rightKey,
      leftKey: relationship.leftKey,
      rightKey: relationship.rightKey,
      selectFields: defaultFields
    });
    
    setSelectedAdditionalFields([]);
    setAdditionalFieldsExpanded(false);
    
    if (table) {
      setJoinSectionExpanded(true);
    }
  };

  const handleAdditionalFieldToggle = (field) => {
    setSelectedAdditionalFields(prev => {
      const isSelected = prev.includes(field);
      return isSelected
        ? prev.filter(f => f !== field)
        : [...prev, field];
    });
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns(prev => {
      const isSelected = prev.includes(columnKey);
      return isSelected
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(availableColumns.map(col => col.key));
  };

  const handleClearAllColumns = () => {
    setSelectedColumns(['studentId']);
  };

  const handleResetToDefault = () => {
    setSelectedColumns([
      'studentId',
      'courseTitle',
      'courseModality',
      'discipline',
      'knowledgeArea',
      'grade',
      'absences',
      'status',
      'academicPeriod',
      'yearValidity'
    ]);
  };

  const visibleColumns = useMemo(() => {
    return availableColumns
      .filter(col => selectedColumns.includes(col.key))
      .map(col => col.key);
  }, [availableColumns, selectedColumns]);

  const getColumnLabel = (columnKey) => {
    const column = availableColumns.find(col => col.key === columnKey);
    return column ? column.label : columnKey;
  };

  const renderCellValue = (item, columnKey) => {
    switch (columnKey) {
      case 'startDate':
      case 'endDate':
      case 'createdAt':
      case 'updatedAt':
        return item[columnKey]; // Já formatado no processedConsolidatedData
      case 'grade':
      case 'courseGrade':
        // Mostrar nota com formatação, mesmo que seja 0
        if (item[columnKey] === '-') return '-';
        const gradeValue = Number(item[columnKey]);
        return isNaN(gradeValue) ? '-' : gradeValue.toFixed(2);
      case 'absences':
        // Mostrar 0 se for 0, apenas '-' se for null/undefined
        return item[columnKey] === '-' ? '-' : item[columnKey];
      default:
        return item[columnKey] || '-';
    }
  };

  // Verifica se um campo joinado é monetário baseado no nome
  const isMonetaryField = (fieldKey) => {
    const monetaryFields = ['ReceivedValue', 'IssuedValue', 'InterestValue', 'DiscountValue'];
    const fieldName = fieldKey.split('_')[1]; // Pega o nome após o prefixo da tabela
    return monetaryFields.includes(fieldName);
  };

  // Renderiza valor de campo joinado com formatação apropriada
  const renderJoinedCellValue = (value, fieldKey) => {
    if (isMonetaryField(fieldKey)) {
      return formatCurrency(value);
    }
    return value || '-';
  };

  const handleApplyJoin = () => {
    const defaultFields = defaultJoinFields[joinConfig.table] || [];
    const allSelectedFields = [...new Set([...defaultFields, ...selectedAdditionalFields])];
    
    if (joinConfig.enabled && allSelectedFields.length === 0) {
      toast.warning("Selecione pelo menos um campo para incluir no JOIN");
      return;
    }
    
    const updatedJoinConfig = {
      ...joinConfig,
      selectFields: allSelectedFields
    };
    
    setJoinConfig(updatedJoinConfig);
    setCurrentPage(1);
    fetchConsolidatedData(1, searchTerm, appliedFilters, updatedJoinConfig, dateFilter, additionalFilters);
    toast.success("Junção de tabelas aplicado com sucesso!");
    setJoinSectionExpanded(false);
  };

  const handleClearJoin = () => {
    setJoinConfig({
      enabled: false,
      table: '',
      type: 'INNER',
      on: 'StudentId',
      leftKey: 'StudentId',
      rightKey: 'StudentId',
      selectFields: []
    });
    setSelectedAdditionalFields([]);
    setAdditionalFieldsExpanded(false);
    setCurrentPage(1);
    fetchConsolidatedData(1, searchTerm, appliedFilters, null, dateFilter, additionalFilters);
    toast.info("JOIN removido");
  };

  const processedConsolidatedData = consolidatedData ? consolidatedData.map((item) => {
    const baseData = {
      studentId: item.StudentId || '-',
      courseTitle: item.CourseTitle || '-',
      courseModality: item.CourseModality || '-',
      discipline: item.Discipline || '-',
      knowledgeArea: item.KnowledgeArea || '-',
      courseType: item.CourseType || '-',
      courseGrade: item.CourseGrade || '-',
      locality: item.Locality || '-',
      grade: item.Grade !== null && item.Grade !== undefined ? item.Grade : '-',
      absences: item.Absences !== null && item.Absences !== undefined ? item.Absences : '-',
      status: item.Status || '-',
      academicPeriod: item.AcademicPeriod || '-',
      yearValidity: item.YearValidity || '-',
      startDate: formatDateOnly(item.StartDate),
      endDate: formatDateOnly(item.EndDate),
      createdAt: formatDateTime(item.CreatedAt),
      updatedAt: formatDateTime(item.UpdatedAt)
    };

    // Adicionar campos de JOIN (prefixados)
    const joinedData = {};
    Object.keys(item).forEach(key => {
      if (key.includes('_')) {
        joinedData[key] = item[key];
      }
    });

    return { ...baseData, ...joinedData };
  }) : [];

  // Extrair colunas joinadas dinamicamente
  const getJoinedColumns = () => {
    if (!consolidatedData || consolidatedData.length === 0) return [];
    
    const joinedCols = [];
    const firstItem = consolidatedData[0];
    
    Object.keys(firstItem).forEach(key => {
      if (key.includes('_')) {
        const [table, field] = key.split('_');
        const fieldInfo = joinFieldsByTable[joinConfig.table]?.find(f => f.value === field);
        
        joinedCols.push({
          key: key,
          label: fieldInfo ? fieldInfo.label : field,
          table: table
        });
      }
    });
    
    return joinedCols;
  };

  const joinedColumns = getJoinedColumns();

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchConsolidatedData(page, searchTerm, appliedFilters, joinConfig, dateFilter, additionalFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchConsolidatedData(1, term, appliedFilters, joinConfig, dateFilter, additionalFilters);
  };

  const getVisiblePages = () => {
    if (!paginationInfo) return [];
    
    const totalPages = paginationInfo.totalPages;
    const current = currentPage;
    let visiblePages = [];
    
    if (totalPages <= 7) {
      visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      if (current <= 4) {
        visiblePages = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (current >= totalPages - 3) {
        visiblePages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        visiblePages = [1, '...', current - 1, current, current + 1, '...', totalPages];
      }
    }
    
    return visiblePages;
  };

  const handleExportCsv = async () => {
    if (!lastApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 3}-01-01`;
      const endDate = `${currentYear + 3}-12-31`;

      const exportQuery = lastApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      // Mapear as chaves das colunas do modelo de dados para os nomes das colunas da API
      const columnToApiFieldMap = {
        studentId: 'StudentId',
        courseTitle: 'CourseTitle',
        courseModality: 'CourseModality',
        discipline: 'Discipline',
        knowledgeArea: 'KnowledgeArea',
        courseType: 'CourseType',
        courseGrade: 'CourseGrade',
        locality: 'Locality',
        grade: 'Grade',
        absences: 'Absences',
        status: 'Status',
        academicPeriod: 'AcademicPeriod',
        yearValidity: 'YearValidity',
        startDate: 'StartDate',
        endDate: 'EndDate',
        createdAt: 'CreatedAt',
        updatedAt: 'UpdatedAt'
      };

      // Construir headers dinamicamente baseado nas colunas visíveis
      const dynamicHeaders = {};
      
      // Adicionar headers das colunas principais visíveis
      visibleColumns.forEach(columnKey => {
        const apiField = columnToApiFieldMap[columnKey];
        if (apiField) {
          const column = availableColumns.find(col => col.key === columnKey);
          if (column) {
            dynamicHeaders[apiField] = column.label;
          }
        }
      });

      // Adicionar headers das colunas de JOIN
      joinedColumns.forEach(col => {
        dynamicHeaders[col.key] = col.label;
      });

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: {
          InstitutionId: institution.id,
          StartDate: startDate,
          EndDate: endDate,
        },
        headers: dynamicHeaders,
        fileName: "dados_academicos_consolidados.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm) {
        exportPayload.parameters.SearchTerm = searchTerm;
      }

      // Incluir filtros aplicados na exportação no formato: Filter0, Filter1, etc.
      appliedFilters.forEach((filter, index) => {
        if (filter.field && filter.values && filter.values.length > 0) {
          const paramKey = `Filter${index}`;
          // Se o operador for 'in' e houver múltiplos valores, passar o array
          // Caso contrário, passar apenas o primeiro valor
          if (filter.operator === 'in' && filter.values.length > 1) {
            exportPayload.parameters[paramKey] = filter.values;
          } else {
            exportPayload.parameters[paramKey] = filter.values[0];
          }
        }
      });

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  // Remove o recorte por arquivo: tira fileId/fileName da URL (o effect de init zera o estado e refaz a busca).
  const handleClearSourceFile = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('fileId');
    next.delete('fileName');
    setSearchParams(next);
  };

  // Inicializar estado a partir da URL
  useEffect(() => {
    const urlSearch = searchParams.get('search') || "";
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlFilterId = searchParams.get('filterId');
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlFileId = searchParams.get('fileId');
    const urlFileName = searchParams.get('fileName');

    setSearchTerm(urlSearch);
    setCurrentPage(urlPage);
    setSourceFile(urlFileId ? { id: urlFileId, name: urlFileName || null } : null);
    
    if (urlStartDate || urlEndDate) {
      setDateFilter({
        startDate: urlStartDate || null,
        endDate: urlEndDate || null
      });
    }
    
    // Se houver filterId na URL, buscar o filtro salvo
    if (urlFilterId && institution?.id) {
      const fetchFilterById = async () => {
        try {
          setLoading(true);
          const savedFilter = await getSavedEducationFilterById({ 
            institutionId: institution.id, 
            filterId: urlFilterId 
          });
          
          const convertedFilters = convertApiFilterToLocal(savedFilter);
          
          try {
            const filterData = typeof savedFilter.filterJson === 'string' 
              ? JSON.parse(savedFilter.filterJson) 
              : savedFilter.filterJson;
            
            if (filterData.joins && filterData.joins.length > 0) {
              const savedJoin = filterData.joins[0];
              setJoinConfig({
                enabled: true,
                table: savedJoin.table,
                type: savedJoin.type,
                on: savedJoin.rightKey || savedJoin.on || 'StudentId',
                leftKey: savedJoin.leftKey || 'StudentId',
                rightKey: savedJoin.rightKey || savedJoin.on || 'StudentId',
                selectFields: savedJoin.selectFields
              });
            }
          } catch (error) {
            console.error("Erro ao extrair JOIN do filtro salvo:", error);
          }
          
          setAppliedFilters(convertedFilters);
          setPendingFilters(convertedFilters);
          setInitialLoadComplete(true);
        } catch (error) {
          console.error("Erro ao buscar filtro por ID:", error);
          toast.error("Erro ao carregar filtro da URL");
          setInitialLoadComplete(true);
        } finally {
          setLoading(false);
        }
      };
      
      fetchFilterById();
    } else {
      // Extrair filtros da URL
      const urlFilters = [];
      let filterIndex = 0;
      while (searchParams.get(`filter${filterIndex}_field`)) {
        const field = searchParams.get(`filter${filterIndex}_field`);
        const operator = searchParams.get(`filter${filterIndex}_operator`) || 'eq';
        const valuesStr = searchParams.get(`filter${filterIndex}_values`);
        const condition = searchParams.get(`filter${filterIndex}_condition`);
        const groupId = searchParams.get(`filter${filterIndex}_groupId`) || filterIndex;
        
        if (field && valuesStr) {
          try {
            const values = JSON.parse(valuesStr);
            const fieldMapping = filterFields.find(f => f.apiField === field);
            const operatorMapping = filterOperators.find(op => op.value === operator);
            const displayValue = Array.isArray(values) ? values.join(', ') : values[0];
            
            urlFilters.push({
              field: field,
              operator: operator,
              values: values,
              label: fieldMapping ? fieldMapping.label : field,
              operatorLabel: operatorMapping ? operatorMapping.label : operator,
              displayValue: displayValue,
              condition: condition || null,
              groupId: parseInt(groupId)
            });
          } catch (e) {
            console.error("Erro ao fazer parse dos valores do filtro:", e);
          }
        }
        filterIndex++;
      }

      setAppliedFilters(urlFilters);
      setPendingFilters(urlFilters);
      setInitialLoadComplete(true);
    }
  }, [searchParams, filterFields, filterOperators, institution?.id, convertApiFilterToLocal]);

  useEffect(() => {
    if (institution?.id && initialLoadComplete) {
      fetchConsolidatedData(currentPage, searchTerm, appliedFilters, joinConfig, dateFilter, additionalFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution?.id, fetchConsolidatedData, currentPage, searchTerm, appliedFilters, initialLoadComplete, dateFilter, additionalFilters]);

  return (
    <div className={styles.consolidatedDataContent}>
      {/* Header com os botões de ação */}
      <HeaderTitle title="Dados Consolidados Acadêmicos" icon={DatabaseIcon}>
        <div className={styles.headerActions}>
          <Button
            icon={FilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            onClick={() => setShowFilterConfigModal(true)}
          />
          <Button
            icon={SavedFiltersIcon}
            text="Filtros Salvos"
            size="normal"
            variation="outline"
            onClick={handleOpenSavedFilters}
          />
          <Button
            icon={ColumnsIcon}
            text="Gerenciar Colunas"
            size="normal"
            variation="outline"
            onClick={() => setShowColumnManagerModal(true)}
          />
          <Button
            icon={MergeIcon}
            text="Mesclar Tabelas"
            size="normal"
            variation="outline"
            onClick={() => setShowJoinTablesModal(true)}
          />
        </div>
      </HeaderTitle>

      {sourceFile && (
        <div className={styles.sourceFileBanner}>
          <span className={styles.sourceFileText}>
            Exibindo apenas os dados importados de{" "}
            <strong>{sourceFile.name || "um arquivo"}</strong>.
          </span>
          <button
            type="button"
            className={styles.sourceFileClear}
            onClick={handleClearSourceFile}
          >
            Ver todos os dados
          </button>
        </div>
      )}

      <div className={styles.consolidatedDataBody}>
      <div className={styles.tableContainer}>
        
        {/* Seção de filtros aplicados */}
        {(appliedFilters.length > 0 || additionalFilters.length > 0) && (
          <div className={styles.appliedFiltersSection}>
            <span className={styles.appliedFiltersLabel}>Filtros aplicados:</span>
            <div className={styles.appliedFiltersList}>
              {appliedFilters.map((filter, index) => (
                <div key={index} className={styles.appliedFilterChip}>
                  <span className={styles.appliedFilterField}>{filter.label}</span>
                  <span className={styles.appliedFilterOperator}>{filter.operator === 'eq' ? 'IGUAL A (=)' : filter.operatorLabel}</span>
                  <span className={styles.appliedFilterValue}>{filter.displayValue}</span>
                  <button
                    className={styles.removeFilterBtn}
                    onClick={() => {
                      const newFilters = appliedFilters.filter((_, i) => i !== index);
                      setAppliedFilters(newFilters);
                      setPendingFilters(newFilters);
                      setCurrentPage(1);
                      updateURL(newFilters);
                      fetchConsolidatedData(1, searchTerm, newFilters, joinConfig, dateFilter, additionalFilters);
                    }}
                    title="Remover filtro"
                  >
                    ×
                  </button>
                </div>
              ))}
              {additionalFilters.map((filter, index) => (
                <div key={`additional-${index}`} className={styles.appliedFilterChip}>
                  <span className={styles.appliedFilterField}>{filter.label}</span>
                  <span className={styles.appliedFilterOperator}>IGUAL A (=)</span>
                  <span className={styles.appliedFilterValue}>{filter.displayValue}</span>
                </div>
              ))}
              <button
                className={styles.clearFiltersBtn}
                onClick={handleClearAllFilters}
                title="Limpar todos os filtros"
              >
                × Limpar Todos
              </button>
            </div>
          </div>
        )}

        {/* Barra de pesquisa e exportação */}
        <div className={styles.tableActions}>
          <div className={styles.searchInputWrapper}>
            <img src={SearchIcon} alt="Pesquisar" className={styles.searchIconTable} />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className={styles.searchInputTable}
            />
          </div>
          <button
            className={styles.exportCsvButton}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? (
              <>
                <img src={loadingGif} alt="Exportando..." className={styles.exportIconTable} />
                Exportando...
              </>
            ) : (
              <>
                <img src={FileExportIcon} alt="Exportar" className={styles.exportIconTable} />
                Exportar CSV
              </>
            )}
          </button>
        </div>
        
        {/* Seção antiga de filtros inline foi substituída por modal */}
        {false && (
        <div className={styles.filterSection}>
          <div className={styles.filterRow}>
            {pendingFilters.length > 0 && (
              <FilterSelect
                icon={FilterIcon}
                label="Condição"
                value={currentFilterCondition}
                loading={false}
                options={[
                  { id: "AND", name: "E (AND)" },
                  { id: "OR", name: "OU (OR)" }
                ]}
                onChange={(e) => setCurrentFilterCondition(e.target.value)}
                hasFetched={true}
              />
            )}
            
            <FilterSelect
              icon={FilterIcon}
              label="Filtrar por campo"
              value={currentFilterField}
              loading={false}
              options={filterFields.map(field => ({ id: field.value, name: field.label }))}
              onChange={(e) => handleFilterFieldChange(e.target.value)}
              hasFetched={true}
            />
            
            {currentFilterField && (
              <>
                <FilterSelect
                  icon={FilterIcon}
                  label="Operador"
                  value={currentFilterOperator}
                  loading={false}
                  options={filterOperators.map(op => ({ id: op.value, name: op.label }))}
                  onChange={(e) => handleOperatorChange(e.target.value)}
                  hasFetched={true}
                />
                
                {['gt', 'lt', 'gte', 'lte'].includes(currentFilterOperator) ? (
                  <>
                    <div className={styles.filterInputWrapper}>
                      <input
                        type="text"
                        placeholder="Digite o valor..."
                        value={currentFilterValue}
                        onChange={(e) => setCurrentFilterValue(e.target.value)}
                        className={styles.filterInput}
                      />
                    </div>
                    
                    {currentFilterValue && (
                      <button
                        className={styles.addFilterButton}
                        onClick={handleAddFilter}
                        title="Adicionar filtro"
                      >
                        Adicionar
                      </button>
                    )}
                  </>
                ) : currentFilterOperator === 'in' ? (
                  <>
                    <FilterSelect
                      icon={FilterIcon}
                      label="Selecionar valores"
                      value={currentFilterValue}
                      loading={loadingFilterValues}
                      options={currentFilterValues.map(value => ({ id: value, name: value }))}
                      onChange={(e) => setCurrentFilterValue(e.target.value)}
                      hasFetched={true}
                    />
                    
                    {currentFilterValue && (
                      <button
                        className={styles.addValueButton}
                        onClick={handleAddValueToMultiple}
                        title="Adicionar valor à lista"
                      >
                        + Adicionar Valor
                      </button>
                    )}
                    
                    {selectedMultipleValues.length > 0 && (
                      <button
                        className={styles.addFilterButton}
                        onClick={handleAddFilter}
                        title="Adicionar filtro completo"
                      >
                        Adicionar Filtro
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <FilterSelect
                      icon={FilterIcon}
                      label="Valor do filtro"
                      value={currentFilterValue}
                      loading={loadingFilterValues}
                      options={currentFilterValues.map(value => ({ id: value, name: value }))}
                      onChange={(e) => setCurrentFilterValue(e.target.value)}
                      hasFetched={true}
                    />
                    
                    {currentFilterValue && (
                      <button
                        className={styles.addFilterButton}
                        onClick={handleAddFilter}
                        title="Adicionar filtro"
                      >
                        Adicionar
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          
          {currentFilterOperator === 'in' && selectedMultipleValues.length > 0 && (
            <div className={styles.selectedValuesContainer}>
              <div className={styles.selectedValuesLabel}>Valores selecionados:</div>
              <div className={styles.selectedValuesList}>
                {selectedMultipleValues.map((value, index) => (
                  <div key={index} className={styles.selectedValueTag}>
                    <span>{value}</span>
                    <button
                      className={styles.selectedValueRemove}
                      onClick={() => handleRemoveValueFromMultiple(value)}
                      title="Remover valor"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {pendingFilters.length > 0 && (
            <div className={styles.appliedFiltersContainer}>
              <div className={styles.appliedFiltersLabel}>Filtros pendentes:</div>
              <div className={styles.appliedFilters}>
                {pendingFilters.map((filter, index) => {
                  const isFirstInGroup = index === 0 || pendingFilters[index - 1].groupId !== filter.groupId;
                  const isLastInGroup = index === pendingFilters.length - 1 || pendingFilters[index + 1].groupId !== filter.groupId;
                  
                  return (
                    <React.Fragment key={index}>
                      {index > 0 && filter.condition === "OR" && (
                        <span className={styles.filterCondition}>
                          OU
                        </span>
                      )}
                      
                      {isFirstInGroup && pendingFilters.filter(f => f.groupId === filter.groupId).length > 1 && (
                        <span className={styles.groupParenthesis}>(</span>
                      )}
                      
                      {!isFirstInGroup && pendingFilters[index - 1].groupId === filter.groupId && (
                        <span className={styles.filterConditionInGroup}>
                          E
                        </span>
                      )}
                      
                      <div className={styles.filterTag}>
                        <span className={styles.filterTagLabel}>{filter.label}</span>
                        <span className={styles.filterTagOperator}>{filter.operatorLabel}</span>
                        <span className={styles.filterTagValue}>{filter.displayValue}</span>
                        <button
                          className={styles.filterTagRemove}
                          onClick={() => handleRemoveFilter(index)}
                          title="Remover filtro"
                        >
                          ×
                        </button>
                      </div>
                      
                      {isLastInGroup && pendingFilters.filter(f => f.groupId === filter.groupId).length > 1 && (
                        <span className={styles.groupParenthesis}>)</span>
                      )}
                    </React.Fragment>
                  );
                })}
                <button
                  className={styles.applyFiltersButton}
                  onClick={handleApplyFilters}
                  title="Aplicar filtros e buscar"
                >
                  Buscar
                </button>
                <button
                  className={styles.clearAllFiltersButton}
                  onClick={handleClearAllFilters}
                  title="Limpar todos os filtros"
                >
                  Limpar Todos
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Seção antiga de JOIN foi substituída por modal */}
        {false && (
        <div className={styles.joinSection}>
          <div 
            className={styles.joinHeader}
            onClick={() => setJoinSectionExpanded(!joinSectionExpanded)}
          >
            <h3 className={styles.joinTitle}>
              <span className={styles.joinToggleIcon}>
                {joinSectionExpanded ? '▼' : '▶'}
              </span>
              Combinar Dados com Outra Tabela
            </h3>
            {joinConfig.enabled && joinConfig.table && (
              <span className={styles.joinActiveBadge}>
                Ativo: {joinTables.find(t => t.value === joinConfig.table)?.label}
              </span>
            )}
          </div>
          
          {joinSectionExpanded && (
            <div className={styles.joinContent}>
              <div className={styles.joinRow}>
                <FilterSelect
                  icon={FilterIcon}
                  label="Adicionar tabela"
                  value={joinConfig.table}
                  loading={false}
                  options={joinTables.map(table => ({ id: table.value, name: table.label }))}
                  onChange={(e) => handleJoinTableChange(e.target.value)}
                  hasFetched={true}
                />
                
                {joinConfig.enabled && joinConfig.table && (
                  <>
                    <FilterSelect
                      icon={FilterIcon}
                      label="Tipo de JOIN"
                      value={joinConfig.type}
                      loading={false}
                      options={[
                        { id: "INNER", name: "Registros em ambas" },
                        { id: "LEFT", name: "Todos da principal" }
                      ]}
                      onChange={(e) => setJoinConfig(prev => ({ ...prev, type: e.target.value }))}
                      hasFetched={true}
                    />

                    <div className={styles.joinRelationshipInfo}>
                      <label>Relacionar por:</label>
                      <span className={styles.joinRelationshipText}>
                        {joinRelationships[joinConfig.table]?.label || 'ID do Aluno'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {joinConfig.enabled && joinConfig.table && joinFieldsByTable[joinConfig.table] && (
                <>
                  <div className={styles.joinFieldsContainer}>
                    <div className={styles.joinFieldsLabel}>
                      Campos incluídos automaticamente:
                    </div>
                    <div className={styles.defaultFieldsList}>
                      {(defaultJoinFields[joinConfig.table] || []).map((fieldValue) => {
                        const field = joinFieldsByTable[joinConfig.table].find(f => f.value === fieldValue);
                        return field ? (
                          <span key={field.value} className={styles.defaultFieldBadge}>
                            ✓ {field.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <div className={styles.additionalFieldsSection}>
                    <div 
                      className={styles.additionalFieldsHeader}
                      onClick={() => setAdditionalFieldsExpanded(!additionalFieldsExpanded)}
                    >
                      <span className={styles.expandIcon}>
                        {additionalFieldsExpanded ? '▼' : '▶'}
                      </span>
                      <span className={styles.additionalFieldsTitle}>
                        Adicionar mais campos 
                        {selectedAdditionalFields.length > 0 && ` (${selectedAdditionalFields.length})`}
                      </span>
                    </div>
                    
                    {additionalFieldsExpanded && (
                      <div className={styles.additionalFieldsList}>
                        {joinFieldsByTable[joinConfig.table]
                          .filter(field => !defaultJoinFields[joinConfig.table]?.includes(field.value))
                          .map((field) => (
                            <label key={field.value} className={styles.joinFieldCheckbox}>
                              <input
                                type="checkbox"
                                checked={selectedAdditionalFields.includes(field.value)}
                                onChange={() => handleAdditionalFieldToggle(field.value)}
                              />
                              <span>{field.label}</span>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className={styles.joinActions}>
                    <button
                      className={styles.applyJoinButton}
                      onClick={handleApplyJoin}
                      disabled={joinConfig.selectFields.length === 0}
                      title="Aplicar JOIN e buscar dados"
                    >
                      Aplicar JOIN
                    </button>
                    <button
                      className={styles.clearJoinButton}
                      onClick={handleClearJoin}
                      title="Remover JOIN"
                    >
                      Limpar JOIN
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Seção antiga de gerenciar colunas foi substituída por modal */}
        {false && (
        <div className={styles.columnsManagement}>
          <div 
            className={styles.columnsHeader}
            onClick={() => setAdditionalColumnsExpanded(!additionalColumnsExpanded)}
          >
            <span className={styles.columnsToggleIcon}>
              {additionalColumnsExpanded ? '▼' : '▶'}
            </span>
            <span className={styles.columnsTitle}>
              Gerenciar Colunas ({selectedColumns.length} de {availableColumns.length} visíveis)
            </span>
          </div>
          
          {additionalColumnsExpanded && (
            <div className={styles.columnsContent}>
              <div className={styles.columnsActions}>
                <button 
                  className={styles.columnActionBtn}
                  onClick={handleSelectAllColumns}
                  title="Selecionar todas as colunas"
                >
                  ✓ Selecionar Todas
                </button>
                <button 
                  className={styles.columnActionBtn}
                  onClick={handleClearAllColumns}
                  title="Desmarcar todas (mantém ID do Aluno)"
                >
                  ✗ Limpar Todas
                </button>
                <button 
                  className={styles.columnActionBtnPrimary}
                  onClick={handleResetToDefault}
                  title="Restaurar seleção padrão"
                >
                  ↺ Restaurar Padrão
                </button>
              </div>

              <div className={styles.columnsSection}>
                <div className={styles.columnsSectionTitle}>
                  Selecione as colunas que deseja visualizar
                </div>
                <div className={styles.columnsGrid}>
                  {availableColumns.map(col => (
                    <label key={col.key} className={styles.columnCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col.key)}
                        onChange={() => handleColumnToggle(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        )}
        
        <div className={styles.tableWrapper}>
          <table className={styles.consolidatedDataTable}>
              <thead>
                <tr>
                {visibleColumns.map(columnKey => (
                  <th key={columnKey} style={columnKey === 'status' ? { textAlign: 'center' } : {}}>{getColumnLabel(columnKey)}</th>
                ))}
                {joinedColumns.map(col => (
                  <th key={col.key} className={styles.joinedColumn}>
                    {col.label} <span className={styles.joinBadge}>JOIN</span>
                  </th>
                ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                  <td colSpan={visibleColumns.length + joinedColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados consolidados acadêmicos...</p>
                    </td>
                  </tr>
                ) : processedConsolidatedData.length > 0 ? (
                  processedConsolidatedData.map((item, index) => (
                    <tr key={`${item.studentId}-${item.discipline}-${index}`}>
                    {visibleColumns.map(columnKey => {
                      if (columnKey === 'status') {
                        return (
                          <td key={columnKey} style={{ textAlign: 'center' }}>
                            <span className={`${styles.statusBadge} ${styles[`status${(item.status || '').replace(/\s+/g, '')}`]}`}>
                          {item.status}
                        </span>
                      </td>
                        );
                      }
                      if (columnKey === 'grade' || columnKey === 'courseGrade') {
                        return (
                          <td key={columnKey} className={styles.gradeCell}>
                            {renderCellValue(item, columnKey)}
                          </td>
                        );
                      }
                      return (
                        <td key={columnKey}>
                          {renderCellValue(item, columnKey)}
                        </td>
                      );
                    })}
                    {joinedColumns.map(col => (
                      <td 
                        key={col.key} 
                        className={styles.joinedColumn}
                        style={{ textAlign: isMonetaryField(col.key) ? 'right' : 'left' }}
                      >
                        {renderJoinedCellValue(item[col.key], col.key)}
                      </td>
                    ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                  <td colSpan={visibleColumns.length + joinedColumns.length} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado consolidado encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </div>
        
        <div className={styles.pagination}>
          {paginationInfo && paginationInfo.totalPages > 1 && (
            <>
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >
                ←
              </button>
              
              {getVisiblePages().map((page, index) => (
                <button
                  key={`page-${page}-${index}`}
                  className={
                    page === '...' 
                      ? styles.paginationDots 
                      : page === currentPage 
                        ? styles.activePage 
                        : styles.paginationButton
                  }
                  onClick={() => page !== '...' && handlePageClick(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
              
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === paginationInfo.totalPages}
                title="Próxima página"
              >
                →
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modais */}
      {showFilterConfigModal && (
        <FilterConfigModal
          onClose={() => setShowFilterConfigModal(false)}
          onApply={handleApplyFiltersFromModal}
          filterFields={filterFields}
          filterOperators={filterOperators}
          institutionId={institution?.id}
          filterOptionsEndpoint="api/v1/EducationInstitution/FilterOptions"
        />
      )}

      {showSavedFiltersModal && (
        <SavedFiltersModal
          onClose={() => setShowSavedFiltersModal(false)}
          onApplyFilter={handleApplySavedFilter}
          filterSource="consolidadosAcademico"
          onCreateFilter={handleOpenSaveFilter}
        />
      )}
      
      {showSaveFilterModal && (
        <SaveFilterModal
          onClose={() => setShowSaveFilterModal(false)}
          onFilterSaved={() => toast.success("Filtro salvo com sucesso!")}
          filterData={convertFiltersToApiFormat()}
        />
      )}

      {showColumnManagerModal && (
        <ColumnManagerModal
          onClose={() => setShowColumnManagerModal(false)}
          onApply={(columns) => {
            setSelectedColumns(columns);
            toast.success("Colunas atualizadas!");
          }}
          availableColumns={availableColumns}
          selectedColumns={selectedColumns}
          defaultColumns={[
            'studentId',
            'courseTitle',
            'courseModality',
            'discipline',
            'knowledgeArea',
            'grade',
            'absences',
            'status',
            'academicPeriod',
            'yearValidity'
          ]}
        />
      )}

      {showJoinTablesModal && (
        <JoinTablesModal
          onClose={() => setShowJoinTablesModal(false)}
          onApply={(config) => {
            setJoinConfig(config);
            setCurrentPage(1);
            fetchConsolidatedData(1, searchTerm, appliedFilters, config, dateFilter, additionalFilters);
            toast.success("Junção de tabelas aplicada!");
          }}
          onClear={() => {
            setJoinConfig({
              enabled: false,
              table: '',
              type: 'INNER',
              on: 'StudentId',
              leftKey: 'StudentId',
              rightKey: 'StudentId',
              selectFields: []
            });
            setCurrentPage(1);
            fetchConsolidatedData(1, searchTerm, appliedFilters, null, dateFilter, additionalFilters);
            toast.info("JOIN removido");
          }}
          joinTables={joinTables}
          joinFieldsByTable={joinFieldsByTable}
          defaultJoinFields={defaultJoinFields}
          joinRelationships={joinRelationships}
          currentJoinConfig={joinConfig}
        />
      )}
      </div>
    </div>
  );
};

export default EducationConsolidatedData;

